import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import { EmptyState, Panel, SettingSwitch, ToolbarButton } from '../../../ui-kit';
import { ConfirmDialog } from '../../../components/dialog';
import { useCan } from '../../../ui/PermissionsProvider';
import {
  fetchPsConfiguration, resetDemoData, setReplyMode, type PsConfiguration, type ReplyMode,
} from '../../../services/policyServicingService';
import CarriersPanel from '../CarriersPanel';
import VoiceAgents from '../VoiceAgents';
import { useLive } from '../rocco/live';

const MEANS: Record<ReplyMode, string> = {
  approve: 'Off: each reply waits under Live requests until someone reads it and sends it, as written or edited.',
  auto: 'On: each reply goes out as soon as it is written. Requests the agent is unsure of still go to a person.',
};

/**
 * How the Policy Servicing agent is set up, exactly as it runs.
 *
 * Every line is read from the running system. The one setting is whether a
 * reply goes out by itself or waits for a person to approve it.
 */
export default function ConfigurationPage() {
  const { data, error, loading, reload } = useLive(fetchPsConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  return (
    <Stack spacing={2}>
      <ReplySetting data={data} loading={loading} onSaved={reload} />

      <Panel title="How a request moves" subtitle="From the email arriving to the reply in the same thread"
        loading={loading} loadingHeight={300}>
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box
              key={rule.when}
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
                columnGap: 2,
                rowGap: 0.25,
                py: 1.25,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rule.then}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      <VoiceAgents
        agents={data?.agents ?? []}
        facts={data?.agent_facts ?? []}
        providerError={data?.provider_error}
        loading={loading}
      />

      {/* The same layout as ROCCO's: what each call finds out beside the guardrails. */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What each call finds out" subtitle="Read from the conversation when the call ends, so nobody types it in"
          loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.finds_out ?? []).map((item) => <Line key={item.key} title={item.label} detail={item.detail} />)}
          </Stack>
        </Panel>

        <Panel title="Guardrails" subtitle="Held on every email and every call, whoever asks" loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.guardrails ?? []).map((rail) => <Line key={rail.title} title={rail.title} detail={rail.detail} />)}
          </Stack>
        </Panel>
      </Box>

      <Panel title="Requests it handles" subtitle="Anything else goes to a person" loading={loading} loadingHeight={200}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {(data?.request_types ?? []).map((type) => (
            <Line key={type.key} title={type.label} detail={`Asks the carrier for ${type.asks}`} />
          ))}
        </Box>
      </Panel>

      {data?.group_request_types?.length ? (
        <Panel title="Requests it handles for employer groups"
          subtitle="From an employer's HR contact, about one of their employees. Answered from our records where we hold the answer, and by calling the carrier where only the carrier knows">
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {data.group_request_types.map((type) => (
              <Line key={type.key} title={type.label} detail={type.answered_from} />
            ))}
          </Box>
        </Panel>
      ) : null}

      {/* Last, and folded: a long list that rarely changes, under a page that
          is otherwise about how the agent works. */}
      <CarriersPanel />

      {/* Below the carrier numbers, and only where the demo employer account exists. */}
      {data?.demo_account ? <DemoData account={data.demo_account} onReset={reload} /> : null}
    </Stack>
  );
}

/**
 * Put the demo employer account back to how a demo opens on it. A call writes
 * what the carrier said onto an employee's record, and the same question is
 * then answered from the record; this takes that off again, so the call can be
 * shown once more. It reaches nothing but that one account.
 */
function DemoData({
  account, onReset,
}: { account: NonNullable<PsConfiguration['demo_account']>; onReset: () => Promise<void> }) {
  const canManage = useCan('manage_workspace');
  const [asking, setAsking] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const reset = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await resetDemoData();
      await onReset();
      setDone(true);
    } catch (err) {
      setDone(false);
      setProblem(err instanceof Error ? err.message : 'Could not reset the demo data');
    } finally {
      setBusy(false);
      setAsking(false);
    }
  };

  const facts = [
    `${account.requests} request${account.requests === 1 ? '' : 's'} about its employees`,
    `${account.changed_by_calls} record${account.changed_by_calls === 1 ? '' : 's'} changed by a call`,
  ].join(' · ');
  const blocked = !canManage ? 'Only a workspace admin can reset the demo data.'
    : account.on_a_call ? 'A carrier is on the phone about one of its employees. Reset once the call has ended.' : null;

  return (
    <Panel
      title="Demo data"
      subtitle={`${account.name} · Group ${account.group_number} · the employer account used to show requests from HR contacts`}
      actions={(
        <ToolbarButton variant="outlined" icon={<RestartAltRounded />} onClick={() => setAsking(true)}
          disabled={Boolean(blocked)} tooltip={blocked ?? undefined} loading={busy}>
          Reset demo data
        </ToolbarButton>
      )}
    >
      <Stack spacing={0.75}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          After a call, what the carrier said stays on the employee&apos;s record, so the same question is answered
          without calling again. Reset puts {account.name} back to how a demo opens on it, so the calls can be shown
          once more.
        </Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          Now: {facts}.
        </Typography>
        {problem ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
        ) : done ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.success })}>
            {account.name} is back to its starting state.
          </Typography>
        ) : null}
      </Stack>
      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        title={`Reset ${account.name}?`}
        description={`${account.name}'s employees, enrollments and invoices go back to how they were before any call, `
          + 'and every request about its employees is removed from Live and Completed requests. '
          + 'Nothing else changes: requests about individual policies, carrier phone numbers and the '
          + "agent's settings are all kept."}
        confirmLabel="Reset demo data"
        onConfirm={reset}
        busy={busy}
      />
    </Panel>
  );
}

/** The one setting: send replies by themselves, or hold each for approval. */
function ReplySetting({
  data, loading, onSaved,
}: { data: PsConfiguration | null; loading: boolean; onSaved: () => Promise<void> }) {
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const current = data?.reply_mode ?? 'approve';

  const change = async (next: ReplyMode) => {
    if (next === current) return;
    setSaving(true);
    setProblem(null);
    try {
      await setReplyMode(next);
      await onSaved();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not change the setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Settings" subtitle="How the agent behaves in this workspace" loading={loading} loadingHeight={72}>
      <SettingSwitch
        id="ps-send-automatically"
        label="Send replies automatically"
        description={MEANS[current]}
        checked={current === 'auto'}
        onChange={(on) => change(on ? 'auto' : 'approve')}
        busy={saving}
        error={problem}
      />
    </Panel>
  );
}

function Line({ title, detail }: { title: string; detail: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{title}</Typography>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{detail}</Typography>
    </Stack>
  );
}
